import * as actionTypes from "../constants";
import * as apis from "../requests/apis";
import * as getPayload from "../requests/payloads";
import * as services from "../services";
import { clientRequest } from "../requests";
import { showToast } from "utils/toast";
import { TOAST_TYPE } from "utils/toast";
import { cognitoLogin, cognitoForgotPassword, cognitoConfirmPassword, cognitoSignOut } from "utils/cognito";

// Register
export const register = (form) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.REGISTER_LOADING });
    const data = getPayload.registerPayload(form);

    let res = null;
    if (form.type === "community")
      // Community: free tier — uses the main register endpoint with Role=6; no subscription needed
      res = await clientRequest(actionTypes.POST, apis.REGISTER_API, data);
    else if (form.type === "organisation")
      res = await clientRequest(actionTypes.POST, apis.ORG_REGISTER_API, {
        Email: data.Email,
        Password: data.Password,
      });
    else if (form.type === "doctor")
      res = await clientRequest(actionTypes.POST, apis.DOC_REGISTER_API, {
        Name: data.FirstName || "",
        Email: data.Email,
        RoleType: "Doctor",
        Password: data.Password,
      });
    else if (form.type === "patient")
      res = await clientRequest(actionTypes.POST, apis.PATIENT_REGISTER_API, {
        FirstName: data.FirstName,
        LastName: data.LastName || "",
        Email: data.Email,
        Password: data.Password,
      });
    else
      res = await clientRequest(actionTypes.POST, apis.USER_REGISTER_API, {
        FirstName: data.FirstName,
        LastName: data.LastName || "",
        Email: data.Email,
        Password: data.Password,
      });
    if (res && res.data) {
      const payload = getPayload.userPayload(res.data);
      services.setLoginUser(payload);
      dispatch({ type: actionTypes.GET_USER_SUCCESS, payload });
      showToast("Registration successful.", TOAST_TYPE.success);
    }
  } catch (err) {
    const msg = err?.response?.data || "Registration failed.";
    showToast(msg, TOAST_TYPE.error);
    dispatch({ type: actionTypes.GET_USER_FAIL, payload: err });
  }
};

// Login
export const login = (form) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.LOGIN_LOADING });
    const cognitoUser = await cognitoLogin(form.email, form.password);

    const PRODUCT = process.env.REACT_APP_PRODUCT || "stroke";
    const adminOnlyRoles = ["superadmin", "brainberry_admin"];
    const isAdmin = adminOnlyRoles.includes(cognitoUser.role);

    if (PRODUCT === "brainberry") {
      // Admin build: only BrainBerry staff allowed
      if (!isAdmin) {
        dispatch({ type: actionTypes.GET_USER_FAIL, payload: null });
        showToast("Access denied. This portal is for BrainBerry staff only.", TOAST_TYPE.error);
        return;
      }
    } else {
      // Client portal: block admin-only accounts from logging in here
      if (isAdmin) {
        dispatch({ type: actionTypes.GET_USER_FAIL, payload: null });
        showToast("Please use admin.cosmaapp.com to sign in with your admin account.", TOAST_TYPE.error);
        return;
      }
    }

    const payload = {
      token: cognitoUser.token,
      userId: cognitoUser.userId,
      email: cognitoUser.email,
      userName: cognitoUser.userName,
      role: cognitoUser.role,
      product: cognitoUser.product || PRODUCT,
      customerId: cognitoUser.customerId,
      subscriptionId: cognitoUser.subscriptionId,
    };
    services.setLoginUser(payload);
    dispatch({ type: actionTypes.GET_USER_SUCCESS, payload });
    showToast("You are successfully logged in.", TOAST_TYPE.success);
  } catch (err) {
    if (err.code === "NewPasswordRequired") {
      showToast("Password reset required. Please contact support.", TOAST_TYPE.error);
      dispatch({ type: actionTypes.GET_USER_FAIL, payload: err });
    } else if (err.code === "MFARequired") {
      showToast("MFA required. Please contact support.", TOAST_TYPE.error);
      dispatch({ type: actionTypes.GET_USER_FAIL, payload: err });
    } else {
      const msg = err.message || "Login failed. Please check your credentials.";
      showToast(msg, TOAST_TYPE.error);
      dispatch({ type: actionTypes.GET_USER_FAIL, payload: err });
    }
  }
};

// Forget Password
export const forgetPassword = (form) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FORGET_PASSWORD_LOADING });
    await cognitoForgotPassword(form.email);
    dispatch({ type: actionTypes.FORGET_PASSWORD_REQ_SUCCESS });
    showToast("Check your email for a verification code.", TOAST_TYPE.success);
  } catch (err) {
    showToast(err.message || "Failed to send reset email.", TOAST_TYPE.error);
    dispatch({ type: actionTypes.FORGET_PASSWORD_REQ_FAIL, payload: err });
  }
};

// Reset Password (Cognito: requires email + confirmation code + new password)
export const resetPassword = (form) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.RESET_PASSWORD_LOADING });
    await cognitoConfirmPassword(form.email, form.token, form.password);
    dispatch({ type: actionTypes.RESET_PASSWORD_SUCCESS });
    showToast("Password changed successfully.", TOAST_TYPE.success);
  } catch (err) {
    showToast(err.message || "Failed to reset password.", TOAST_TYPE.error);
    dispatch({ type: actionTypes.RESET_PASSWORD_FAIL, payload: err });
  }
};

// Get User
export const getUser = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FETCH_USER });

    // Dev bypass — auto-login as superadmin without Cognito
    if (process.env.REACT_APP_DEV_BYPASS === "true") {
      const bypass = {
        token: "dev-bypass",
        userId: "00000000-0000-0000-0000-000000000001",
        userName: "Dev SuperAdmin",
        email: "softwares@brainberry.co.uk",
        role: "superadmin",
        subscriptionId: null,
      };
      services.setLoginUser(bypass);
      dispatch({ type: actionTypes.GET_USER_SUCCESS, payload: bypass });
      return;
    }

    const user = services.getLoginUser();
    if (user.userId) {
      dispatch({
        type: actionTypes.GET_USER_SUCCESS,
        payload: user,
      });
    } else {
      dispatch({
        type: actionTypes.LOGOUT_SUCCESS,
      });
    }
  } catch (err) {
    dispatch({
      type: actionTypes.GET_USER_FAIL,
      payload: err,
    });
  }
};

// Logout
export const logout = () => async (dispatch) => {
  try {
    cognitoSignOut();
    services.removeLoginUser();
    dispatch({ type: actionTypes.GET_REPORT_CLEAR });
    dispatch({ type: actionTypes.LOGOUT_SUCCESS });
  } catch (err) {
    dispatch({ type: actionTypes.LOGOUT_FAIL, payload: err });
  }
};

// Create Org
export const createOrg = (form) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CREATING_ORG });
    const data = getPayload.usersPayload(form.name, form.email);
    const res = await clientRequest(actionTypes.POST, apis.CREATE_ORG_API, data);
    showToast("Organisation successfully added.", TOAST_TYPE.success);
    dispatch({ type: actionTypes.CREATE_ORG_SUCCESS, payload: res.data });
  } catch (err) {
    showToast(err?.response?.data || "Failed to create organisation.", TOAST_TYPE.error);
    dispatch({ type: actionTypes.CREATE_ORG_FAIL, payload: err });
  }
};

// Create Doctor
export const createDoctor = (form) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CREATING_DOCTOR });
    const data = getPayload.usersPayload(form.name, form.email);
    const res = await clientRequest(actionTypes.POST, apis.CREATE_DOCTOR_API, {
      Name: form?.name,
      Email: form?.email,
      OrgEmail: form?.GetEmail || "",
    });
    showToast("Doctor successfully added.", TOAST_TYPE.success);
    dispatch({ type: actionTypes.CREATE_DOCTOR_SUCCESS, payload: res.data });
  } catch (err) {
    showToast(err?.response?.data || "Failed to add doctor.", TOAST_TYPE.error);
    dispatch({ type: actionTypes.CREATE_DOCTOR_FAIL, payload: err });
  }
};

// Create Patient
export const createPatient = (form) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CREATING_PATIENT });
    const data = getPayload.usersPayload(form.name, form.email);
    const res = await clientRequest(actionTypes.POST, apis.CREATE_PATIENT_API, {
      Name: form?.name,
      Email: form?.email,
      SubscriptionId: form?.SubscriptionId || "",
    });
    showToast("Patient successfully added.", TOAST_TYPE.success);
    dispatch({ type: actionTypes.CREATE_PATIENT_SUCCESS, payload: res.data });
  } catch (err) {
    showToast(err?.response?.data || "Failed to add patient.", TOAST_TYPE.error);
    dispatch({ type: actionTypes.CREATE_PATIENT_FAIL, payload: err });
  }
};
